import {
  AdminDollarInput2,
  AdminInput,
  Button,
  CheckmarkInput,
  CollapsibleComponent,
  DebtRepaymentPlan,
  DebtRepaymentPlanRequest,
  DebtRepaymentProjection,
  DebtRepaymentService,
  DebtRepaymentSuggestionRequest,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  Modal,
  P,
  displayCentsInDollars,
  lightTheme,
  useMeasuredHeight,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import {
  BALANCE_TIMES,
  PLAN_AMOUNT_MAX,
  PLAN_AMOUNT_MIN,
  PLAN_NOTE_MAX,
  SERVICE_PLAN_TITLES,
  serviceDebtTitle,
} from '../helpers/repaymentPlanLabels';
import { useRepaymentPlanSuggestion } from '../hooks/useRepaymentPlanSuggestion';
import {
  MethodCheck,
  MethodExtra,
  MethodLabel,
  MethodRow,
} from './RepaymentPlanModal.styled';
import { RepaymentSuggestionModal } from './RepaymentSuggestionModal';
import { SuggestPlanBox } from './SuggestPlanBox';

type Props = {
  isOpen: boolean;
  /** For the AI suggestion. */
  locationId: number;
  /** Which debt the plan repays. */
  service: DebtRepaymentService;
  /** The plan being edited; null starts a new one. */
  plan: DebtRepaymentPlan | null;
  /** The debt a plan can repay now, for the summary at the top. */
  repayableDebt: number;
  /** SaaS only: a vDrive plan runs too, and takes from the same money first. */
  runsAfterVDrive?: boolean;
  busy: boolean;
  onClose: () => void;
  onConfirm: (data: DebtRepaymentPlanRequest) => Promise<boolean>;
};

type Form = {
  fromLocationBalance: boolean;
  /** Cents; 0 = no limit. */
  dailyLimit: number;
  perOrder: boolean;
  perOrderAmount: number;
  perDelivery: boolean;
  perDeliveryAmount: number;
  note: string;
};

const formFor = (plan: DebtRepaymentPlan | null): Form => ({
  fromLocationBalance: plan?.fromLocationBalance ?? false,
  dailyLimit: plan?.dailyBalanceLimit ?? 0,
  perOrder: plan?.perOrderAmount != null,
  perOrderAmount: plan?.perOrderAmount ?? 0,
  perDelivery: plan?.perDeliveryAmount != null,
  perDeliveryAmount: plan?.perDeliveryAmount ?? 0,
  note: plan?.note ?? '',
});

const inRange = (amount: number) =>
  amount >= PLAN_AMOUNT_MIN && amount <= PLAN_AMOUNT_MAX;

const RANGE = `${displayCentsInDollars(
  PLAN_AMOUNT_MIN
)} - ${displayCentsInDollars(PLAN_AMOUNT_MAX)}`;

/** The daily limit to send: only with the vPay Card ticked, null = none. */
const dailyLimitFor = (form: Form) =>
  form.fromLocationBalance && form.dailyLimit > 0 ? form.dailyLimit : null;

/**
 * What the form holds, as the suggestion's scenario: only methods that could
 * be saved (an amount out of range would 400 the whole call), and nothing at
 * all while none is ticked, since sending any one field asks for a scenario.
 * The daily limit only goes along when there is one.
 */
const scenarioFor = (form: Form): DebtRepaymentSuggestionRequest => {
  const perOrderAmount =
    form.perOrder && inRange(form.perOrderAmount) ? form.perOrderAmount : null;
  const perDeliveryAmount =
    form.perDelivery && inRange(form.perDeliveryAmount)
      ? form.perDeliveryAmount
      : null;
  if (
    !form.fromLocationBalance &&
    perOrderAmount == null &&
    perDeliveryAmount == null
  )
    return {};
  const dailyBalanceLimit = dailyLimitFor(form);
  return {
    fromLocationBalance: form.fromLocationBalance,
    ...(dailyBalanceLimit != null && { dailyBalanceLimit }),
    perOrderAmount,
    perDeliveryAmount,
  };
};

/**
 * Start a debt repayment plan for the old vDrive debt or the past SaaS fees,
 * or change the active one. Any mix of the three methods, at least one; the
 * vPay Card can be capped at so much a day. An edit sends the whole plan
 * again and applies from then on. No receivables password: the routes only
 * want the admin role.
 */
export const RepaymentPlanModal = ({
  isOpen,
  locationId,
  service,
  plan,
  repayableDebt,
  runsAfterVDrive = false,
  busy,
  onClose,
  onConfirm,
}: Props) => {
  const [form, setForm] = useState<Form>(() => formFor(plan));
  const set = (patch: Partial<Form>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const suggestion = useRepaymentPlanSuggestion(locationId);
  const { reset: resetSuggestion } = suggestion;
  const [target, setTarget] = useState<number | null>(null);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  const limits = suggestion.data?.limits;

  // Every opening starts from what is saved, not from an abandoned edit, and
  // without the last opening's suggestion.
  useEffect(() => {
    if (!isOpen) return;
    setForm(formFor(plan));
    setTarget(null);
    setApplied(false);
    setSuggestionOpen(false);
    resetSuggestion();
  }, [isOpen, plan, resetSuggestion]);

  const suggest = () => {
    setSuggestionOpen(true);
    suggestion.mutate({
      // vDrive is the default; left out so an older backend never sees it.
      ...(service === 'saas' && { service }),
      ...scenarioFor(form),
      ...(target != null && { targetMonths: target }),
    });
  };

  const applySuggestion = (projection: DebtRepaymentProjection) => {
    // An amount the AI turned off keeps whatever was typed, so ticking it
    // again brings that back. The daily limit is not an "off": none means
    // the vPay Card takes everything, so it is cleared.
    set({
      fromLocationBalance: projection.fromLocationBalance,
      dailyLimit: projection.dailyBalanceLimit ?? 0,
      perOrder: projection.perOrderAmount != null,
      perOrderAmount: projection.perOrderAmount ?? form.perOrderAmount,
      perDelivery: projection.perDeliveryAmount != null,
      perDeliveryAmount: projection.perDeliveryAmount ?? form.perDeliveryAmount,
    });
    setApplied(true);
    setSuggestionOpen(false);
  };

  // Nothing to collect with: the button stays disabled until one is ticked.
  const noneSelected =
    !form.fromLocationBalance && !form.perOrder && !form.perDelivery;

  const submit = async () => {
    if (noneSelected) return ErrorToast('Choose at least one way to collect');
    if (form.perOrder && !inRange(form.perOrderAmount))
      return ErrorToast(`The amount per order has to be ${RANGE}`);
    if (form.perDelivery && !inRange(form.perDeliveryAmount))
      return ErrorToast(`The amount per delivery has to be ${RANGE}`);

    const note = form.note.trim();
    const dailyBalanceLimit = dailyLimitFor(form);
    const ok = await onConfirm({
      fromLocationBalance: form.fromLocationBalance,
      perOrderAmount: form.perOrder ? form.perOrderAmount : null,
      perDeliveryAmount: form.perDelivery ? form.perDeliveryAmount : null,
      // Sent when there is a limit, or to clear the plan's own; never
      // otherwise, so a backend without it keeps taking the plain plan.
      ...((dailyBalanceLimit != null || plan?.dailyBalanceLimit != null) && {
        dailyBalanceLimit,
      }),
      note: note || null,
      // Create only: an edit never changes it, and vDrive is the default.
      ...(!plan && service === 'saas' && { service }),
    });
    if (ok) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header={`${plan ? 'Edit' : 'Create'} ${SERVICE_PLAN_TITLES[service]}`}
    >
      <Div $flex $column $gap="16px">
        <Div $box $p="12px 16px">
          <Disclaimer $m="0">
            {serviceDebtTitle(service)} left to repay
          </Disclaimer>
          <H5 $m="0" $color={lightTheme.primary}>
            {displayCentsInDollars(repayableDebt)}
          </H5>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {service === 'saas'
              ? 'Oldest fee first. Only the fees billed before this month that are open when the plan starts; fees billed later go through ACH as usual.'
              : 'Oldest charge first. The vDrive reserve is not part of the plan.'}
          </Disclaimer>
          {runsAfterVDrive && (
            <Disclaimer $m="4px 0 0" $color={lightTheme.lightGray}>
              The vDrive plan runs too and goes first: this plan gets what it
              leaves.
            </Disclaimer>
          )}
        </Div>

        {repayableDebt > 0 && (
          <SuggestPlanBox
            target={target}
            onTargetChange={setTarget}
            busy={suggestion.isPending}
            onSuggest={suggest}
            onShowLast={
              suggestion.data ? () => setSuggestionOpen(true) : undefined
            }
            applied={applied}
            saveLabel={plan ? 'you save' : 'you create the plan'}
          />
        )}

        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {plan ? (
            <>
              Changes apply from now on.
              <br />
              What was already collected stays repaid.
            </>
          ) : (
            <>
              Combine as many as you like.
              <br />
              The plan completes on its own once the debt is paid off.
            </>
          )}
        </Disclaimer>

        {/* The three ways to collect sit closer to each other than to the
            rest of the form: they are one choice. */}
        <Div $flex $column $gap="8px">
          <Method
            ticked={form.fromLocationBalance}
            onToggle={(value) => set({ fromLocationBalance: value })}
            title="From the vGrubs balance (vPay Card)"
            description={`Every day at ${BALANCE_TIMES} New York, takes whatever is on the vGrubs balance, up to what is still owed and the daily limit. Runs after the 10:00 vDrive top-up, so the top-up comes first.`}
          >
            <DailyLimitField
              value={form.dailyLimit}
              onChange={(value) => set({ dailyLimit: value })}
              recommendedMax={limits?.maxDailyBalanceLimit}
            />
          </Method>

          <Method
            ticked={form.perOrder}
            onToggle={(value) => set({ perOrder: value })}
            title="An amount per order"
            description="Withheld once from the payout of every virtual vGrubs brand order. When the payout is smaller, the whole payout is taken."
          >
            <AmountField
              value={form.perOrderAmount}
              onChange={(value) => set({ perOrderAmount: value })}
              recommendedMax={limits?.maxPerOrderAmount}
            />
          </Method>

          <Method
            ticked={form.perDelivery}
            onToggle={(value) => set({ perDelivery: value })}
            title="An amount per delivery"
            description="Charged to the vDrive balance on every completed delivery, even into the reserve. Skipped while deliveries are paid by the vGrubs vDrive card."
          >
            <AmountField
              value={form.perDeliveryAmount}
              onChange={(value) => set({ perDeliveryAmount: value })}
              recommendedMax={limits?.maxPerDeliveryAmount}
            />
          </Method>
        </Div>

        <AdminInput
          type="textarea"
          label="Note (optional)"
          placeholder="e.g. Agreed with the owner on Oct 7"
          value={form.note}
          maxLength={PLAN_NOTE_MAX}
          $textareaHeight={90}
          onChange={(event) => set({ note: event.target.value })}
        />

        {/* Kept apart from the note: right under its counter the primary
            button read as part of the field. */}
        <Div $flex $gap="12px" $mt="16">
          <Button
            $selected
            $gray
            $w="calc(50% - 6px)"
            $wSM="calc(50% - 6px)"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            $selected
            $w="calc(50% - 6px)"
            $wSM="calc(50% - 6px)"
            $loading={busy}
            disabled={busy || noneSelected}
            onClick={submit}
          >
            {plan ? 'Save' : 'Create plan'}
          </Button>
        </Div>
      </Div>

      <RepaymentSuggestionModal
        isOpen={isOpen && suggestionOpen}
        service={service}
        closeModal={() => setSuggestionOpen(false)}
        isGenerating={suggestion.isPending}
        data={suggestion.data}
        error={suggestion.error}
        onRetry={() => suggestion.mutate(suggestion.variables ?? {})}
        onApply={applySuggestion}
      />
    </Modal>
  );
};

type MethodProps = {
  ticked: boolean;
  onToggle: (value: boolean) => void;
  title: string;
  description: string;
  /** Shown under the text while ticked. */
  children?: React.ReactNode;
};

const Method = ({
  ticked,
  onToggle,
  title,
  description,
  children,
}: MethodProps) => {
  const { ref, height } = useMeasuredHeight<HTMLDivElement>();

  return (
    <MethodRow $ticked={ticked}>
      <MethodLabel>
        <MethodCheck>
          <CheckmarkInput checked={ticked} onChange={onToggle} />
        </MethodCheck>
        <Div $width="100%" $minWidth="0">
          <P $m="0" $bold>
            {title}
          </P>
          <Disclaimer $m="2px 0 0" $color={lightTheme.lightGray}>
            {description}
          </Disclaimer>
        </Div>
      </MethodLabel>
      {/* Always rendered, so ticking slides the amount open instead of
          popping it in; the 12px above it only exists while open. */}
      {children && (
        <CollapsibleComponent
          expanded={ticked}
          elementHeight={height}
          marginTop="12px"
        >
          <MethodExtra ref={ref}>{children}</MethodExtra>
        </CollapsibleComponent>
      )}
    </MethodRow>
  );
};

/**
 * The most the vPay Card gives a day, both runs together. Empty (0) is no
 * limit: the field shows "No limit" then.
 */
const DailyLimitField = ({
  value,
  onChange,
  recommendedMax,
}: {
  value: number;
  onChange: (value: number) => void;
  recommendedMax?: number | null;
}) => (
  <Div $flex $alignCenter $gap="12px">
    <Div $width="90px" $flexNone>
      <AdminDollarInput2
        value={value}
        onValueChange={onChange}
        placeholder="No limit"
        $small
        $textCenter
      />
    </Div>
    <Disclaimer $m="0" $color={lightTheme.lightGray}>
      Daily limit, optional
      {recommendedMax != null && (
        <>
          <br />
          AI recommends up to {displayCentsInDollars(recommendedMax)}
        </>
      )}
    </Disclaimer>
  </Div>
);

/** Typed in dollars, sent in cents. */
const AmountField = ({
  value,
  onChange,
  recommendedMax,
}: {
  value: number;
  onChange: (value: number) => void;
  /** The AI suggestion's limit, once there is one; a hint, not a rule. */
  recommendedMax?: number | null;
}) => (
  // The allowed range beside the field, not under it: one line instead of
  // two inside the option.
  <Div $flex $alignCenter $gap="12px">
    <Div $width="90px" $flexNone>
      <AdminDollarInput2
        value={value}
        max={PLAN_AMOUNT_MAX}
        onValueChange={onChange}
        $small
        $textCenter
      />
    </Div>
    <Disclaimer $m="0" $color={lightTheme.lightGray}>
      {RANGE}
      {recommendedMax != null && (
        <>
          <br />
          AI recommends up to {displayCentsInDollars(recommendedMax)}
        </>
      )}
    </Disclaimer>
  </Div>
);
